"use client";
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UI_MAX_FILE_BYTES, ALLOWED_EXTENSIONS } from '@/lib/knowledgeConfig';
import { createKnowledge, patchKnowledge, uploadKnowledgeFile } from '@/lib/api';

type KnowledgeItem = { id: string; type: 'file'|'url'|'code'; name: string; url?: string; text?: string };

export default function KnowledgeModal({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved?: (setObj: any)=>void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [system_prompt, setSystem_prompt] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [ispublic, setIspublic] = useState<'private' | 'public'>('private');
  const [icon, setIcon] = useState<string | undefined>();
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [knowledgeId, setKnowledgeId] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);

  const maxMb = useMemo(() => Math.round(UI_MAX_FILE_BYTES / (1024*1024)), []);

  if (!open) return null;

  const handleUpload = async (file: File, opts?: { toKnowledge?: boolean }) => {
    if (opts?.toKnowledge) {
      let kid = knowledgeId;
      if (!kid) {
        kid = await createKnowledge();
        setKnowledgeId(kid);
      }
      await uploadKnowledgeFile(kid!, file);
      return { uploaded: true } as any;
    }
    const fd = new FormData();
    fd.append('file', file, file.name);
    const res = await fetch('/api/files/upload', { method: 'POST', body: fd });
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    return data?.url || data;
  };

  const onUploadIcon = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/png,image/jpeg';
      input.onchange = async () => {
        const f = input.files?.[0];
        if (!f) return;
        setBusy(true);
        try { const url = await handleUpload(f); setIcon(url); } finally { setBusy(false); }
      };
      input.click();
    } catch {}
  };

  const onUploadKnowledge = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      input.accept = ALLOWED_EXTENSIONS.map(x => `.${x}`).join(',');
      input.onchange = async () => {
        const fs = Array.from(input.files || []);
        if (!fs.length) return;
        setBusy(true);
        try {
          for (const f of fs) {
            await handleUpload(f, { toKnowledge: true });
            setItems((prev) => {
              const item: KnowledgeItem = { id: `${Date.now()}-${Math.random()}` , type: 'file', name: f.name };
              const next = [item, ...prev];
              return next.slice(0, 200);
            });
          }
        } finally { setBusy(false); }
      };
      input.click();
    } catch {}
  };

  const handleDroppedFiles = async (files: File[]) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const f of files) {
        await handleUpload(f, { toKnowledge: true });
        setItems((prev) => {
          const item: KnowledgeItem = { id: `${Date.now()}-${Math.random()}` , type: 'file', name: f.name };
          const next = [item, ...prev];
          return next.slice(0, 200);
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const onAddUrl = () => {
    const u = prompt('Add knowledge URL');
    if (u && u.trim()) setItems((k)=>[{ id: `${Date.now()}-${Math.random()}`, type: 'url', name: u.trim(), url: u.trim() }, ...k]);
  };

  const onAddText = () => {
    const t = prompt('Paste text');
    if (t && t.trim()) setItems((k)=>[{ id: `${Date.now()}-${Math.random()}`, type: 'code', name: `note-${k.length+1}.txt`, text: t.trim() }, ...k]);
  };

  const onSave = async () => {
    let kid = knowledgeId;
    if (!kid) {
      // Try to create knowledge with payload in one shot so backend saves fields at creation
      try {
        const uid = (() => { try { return (typeof window !== 'undefined') ? localStorage.getItem('user_id') || undefined : undefined; } catch { return undefined; } })();
        const payload: any = {
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          system_prompt: system_prompt.trim() || undefined,
          temperature,
          icon,
          ispublic: ispublic === 'public',
        };
        const res = await fetch('/api/knowledge', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(uid ? { 'x-user-id': uid, 'x-user': uid } : {}) },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const data: any = await res.json().catch(() => ({}));
          kid = String(data?.knowledge_id ?? data?.id ?? data?.knowledgeId ?? data?.uuid ?? '');
          if (kid) setKnowledgeId(kid);
        }
      } catch {}
      // Fallback: create id first if not obtained
      if (!kid) {
        kid = await createKnowledge();
        setKnowledgeId(kid);
      }
    }
    // Ensure fields are saved even if backend ignored POST payload
    try {
      await patchKnowledge(kid!, {
        system_prompt: system_prompt.trim() || undefined,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        temperature,
        icon: icon,
      });
    } catch {}
    try { localStorage.setItem('active_knowledge_id', String(kid)); } catch {}
    const setObj: any = { id: kid, name: title.trim() || kid, description: description.trim() || undefined, items, icon, temperature, system_prompt: system_prompt.trim() || undefined, system: system_prompt.trim() || undefined };
    onSaved?.(setObj);
    onClose();
  };

  const overlay = (
    <div className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center cursor-pointer" onClick={onClose}>
      <div className="w-[min(900px,95vw)] max-h-[92vh] overflow-y-auto rounded-lg border border-gray-700 bg-gray-900 text-gray-100" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="font-semibold">Add Knowledge</div>
          <button onClick={onClose} className="px-2 py-1 rounded hover:bg-gray-800">✕</button>
        </div>
        <div className="px-4 pt-3">
          <div className="flex gap-6 text-sm border-b border-gray-700">
            <div className="px-2 py-2 border-b-2 border-blue-400">Setting</div>
            <div className="px-2 py-2 text-gray-500">Embedded on your website</div>
            <div className="px-2 py-2 text-gray-500">History</div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="rounded-md bg-blue-900/20 border border-blue-700 text-blue-200 p-3 text-sm">
            Knowledge build to a Retrieval Augmented Generation (RAG). By LLMs is sophisticated question-answering (Q&A) chatbots. These are applications that can answer questions about specific your source data information.
          </div>

          <div>
            <Label className="mb-3">Title</Label>
            <Input placeholder="Enter Title" value={title} onChange={(e)=>setTitle(e.target.value)} />
          </div>
          <div>
            <Label className="mb-3">Description</Label>
            <textarea className="w-full bg-black/40 border border-gray-700 rounded-md p-2 min-h-[80px]" placeholder="Enter Description" value={description} onChange={(e)=>setDescription(e.target.value)} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Label className="mb-3">System Instruction (Optional)</Label>
              <span className="text-xs text-gray-400">ตัวอย่างการสร้าง System Instruction</span>
            </div>
            <textarea className="w-full bg-black/40 border border-gray-700 rounded-md p-2 min-h-[90px]" placeholder="Set the system instruction during model initialization" value={system_prompt} onChange={(e)=>setSystem_prompt(e.target.value)} />
            <div className="text-[11px] text-gray-500 mt-1">กำหนดไม่เกิน 2048 ตัวอักษร</div>
          </div>

          <div>
            <div className="flex items-center justify-between text-sm">
              <div>Temperature (Optional) <span className="text-gray-400">Default</span></div>
              <div className="text-blue-300 font-mono">{temperature.toFixed(2)}</div>
            </div>
            <input type="range" min={0} max={2} step={0.01} value={temperature} onChange={(e)=>setTemperature(Number(e.target.value))} className="w-full" />
            <div className="flex justify-between text-[11px] text-gray-500"><span>Low</span><span>Medium</span><span>High</span></div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" disabled={busy} onClick={onUploadIcon}>Upload Icon</Button>
            {icon && <span className="text-xs text-gray-400">Saved to {icon}</span>}
          </div>

          <div
            className={`rounded-md border-2 border-dashed p-3 ${dragging ? 'border-blue-500 bg-blue-900/10' : 'border-gray-700'}`}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current++; setDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current = Math.max(0, dragCounter.current - 1); if (dragCounter.current === 0) setDragging(false); }}
            onDrop={async (e) => {
              e.preventDefault(); e.stopPropagation(); dragCounter.current = 0; setDragging(false);
              const dt = e.dataTransfer;
              const files = dt?.files ? Array.from(dt.files) : [];
              if (files.length) await handleDroppedFiles(files);
            }}
          >
            <div className="text-sm text-blue-300 mb-2">แหล่งข้อมูล:</div>
            <div className="text-xs text-gray-400 mb-2">Knowledge Data: (File support: {ALLOWED_EXTENSIONS.join(', ')} ) ไม่เกิน {maxMb}MB ต่อไฟล์</div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={busy} onClick={onUploadKnowledge}>Upload File Knowledge Data</Button>
              <Button variant="outline" onClick={onAddUrl}>Add URL</Button>
              <Button variant="outline" onClick={onAddText}>Add Text</Button>
            </div>
            <div className="text-[11px] text-gray-500 mt-2">หรือ ลากไฟล์มาวางในกรอบนี้</div>
            <div className="mt-2 space-y-1">
              {items.map((it) => (
                <div key={it.id} className="flex items-center justify-between rounded-md border border-gray-700 px-2 py-1 bg-gray-800/40 text-sm">
                  <div className="truncate"><span className="text-gray-400 mr-2">[{it.type}]</span><span className="font-mono">{it.name}</span></div>
                  <button className="text-red-300" onClick={() => setItems((k)=>k.filter(x => x.id !== it.id))}>Remove</button>
                </div>
              ))}
              {items.length === 0 && <div className="text-xs text-gray-500">ยังไม่มีไฟล์/URL/ข้อความ</div>}
            </div>
          </div>

          <div>
            <div className="text-sm mb-1">Shared</div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="radio" name="vis" checked={ispublic==='private'} onChange={()=>setIspublic('private')} /> Private</label>
              <label className="flex items-center gap-2"><input type="radio" name="vis" checked={ispublic==='public'} onChange={()=>setIspublic('public')} /> Public</label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 pb-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={onSave} disabled={!title.trim() || busy}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
  return typeof window !== 'undefined' ? createPortal(overlay, document.body) : overlay;
}
